import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Maximize2, PhoneOff, Video } from "lucide-react";
import { useMeetingDockStore } from "../store/meetingDockStore";
import { useActiveMeeting } from "./MeetingRoomProvider";
import { MeetingRoomView } from "./MeetingRoomView";

// The dock never gets narrower than this, and always leaves at least
// MIN_CONTENT px for the page on the left.
const MIN_DOCK = 320;
const MIN_CONTENT = 420;

/**
 * The collapsed meeting: a draggable divider + resizable right-hand panel that
 * sits next to whatever page is open, so the user can keep the call running
 * while working elsewhere. Rendered inside MeetingRoomProvider, so it draws the
 * same live `room` as the full-screen view — collapsing/expanding never
 * reconnects. Desktop-only (md+); shows only when the meeting is docked.
 */
export function MeetingDockAside() {
  const navigate = useNavigate();
  const mode = useMeetingDockStore((s) => s.mode);
  const width = useMeetingDockStore((s) => s.dockWidth);
  const setWidth = useMeetingDockStore((s) => s.setDockWidth);
  const expand = useMeetingDockStore((s) => s.expand);
  const leaveStore = useMeetingDockStore((s) => s.leave);
  const active = useActiveMeeting();
  const [dragging, setDragging] = useState(false);
  const [liveWidth, setLiveWidth] = useState(width);

  useEffect(() => {
    if (!dragging) setLiveWidth(width);
  }, [width, dragging]);

  if (mode !== "docked" || !active) return null;

  const clampW = (w: number) =>
    Math.max(MIN_DOCK, Math.min(w, window.innerWidth - MIN_CONTENT));

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(true);
    const onMove = (ev: MouseEvent) =>
      setLiveWidth(clampW(window.innerWidth - ev.clientX));
    const onUp = (ev: MouseEvent) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setDragging(false);
      setWidth(clampW(window.innerWidth - ev.clientX));
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const onExpand = () => {
    expand();
    navigate(`/meetings/room/${active.occurrenceId}`);
  };

  const onLeave = () => {
    active.room.leave();
    leaveStore();
  };

  return (
    <>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Przeciągnij, aby zmienić szerokość panelu spotkania"
        onMouseDown={startDrag}
        className={`hidden w-1.5 shrink-0 cursor-col-resize border-l border-[var(--color-border)] transition-colors hover:bg-[var(--color-primary)] md:block ${
          dragging ? "bg-[var(--color-primary)]" : "bg-[var(--color-bg-muted)]"
        }`}
      />
      <aside
        style={{ width: liveWidth }}
        className="hidden shrink-0 flex-col border-l border-[var(--color-border)] bg-[var(--color-bg-surface)] md:flex"
      >
        <MeetingRoomView
          occurrenceId={active.occurrenceId}
          room={active.room}
          devices={active.devices}
          variant="dock"
          onDock={() => {}}
          onExpand={onExpand}
          onLeave={onLeave}
        />
      </aside>
      {dragging && <div className="fixed inset-0 z-50 cursor-col-resize" />}

      {/* The side panel is desktop-only; on a phone a docked call would be
          invisible (and still capturing the mic). This bottom bar keeps it
          reachable — expand it back or hang up. */}
      <div className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-2 border-t border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 shadow-lg md:hidden">
        <Video size={16} className="shrink-0 text-[var(--color-primary)]" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-[var(--color-text-primary)]">
          Trwa spotkanie
        </span>
        <button
          type="button"
          onClick={onExpand}
          className="flex items-center gap-1 rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-xs text-[var(--color-text-secondary)]"
        >
          <Maximize2 size={13} /> Wróć
        </button>
        <button
          type="button"
          onClick={onLeave}
          className="flex items-center gap-1 rounded-md bg-red-600 px-2.5 py-1.5 text-xs font-medium text-white"
        >
          <PhoneOff size={13} /> Zakończ
        </button>
      </div>
    </>
  );
}
